/**
 * **한/영 전환을 깜빡하고 친 영문 자판 글자를 한글로** (사용자 2026-10-27 "물발이면 anfqkf 해도 되게").
 *
 * 두벌식 자판 자리 그대로 글자를 자모로 바꾸고, 한글 입력기처럼 초성·중성·종성을 모아 글자로 만든다.
 * `anfqkf` → ㅁㅜㄹㅂㅏㄹ → `물발`. 대문자는 쌍자음·ㅒ·ㅖ 자리(QWERTOP)만 Shift 로 보고, 나머지 대문자는 소문자와 같다.
 */
const KEY: Record<string, string> = {
    q: 'ㅂ', w: 'ㅈ', e: 'ㄷ', r: 'ㄱ', t: 'ㅅ', y: 'ㅛ', u: 'ㅕ', i: 'ㅑ', o: 'ㅐ', p: 'ㅔ',
    a: 'ㅁ', s: 'ㄴ', d: 'ㅇ', f: 'ㄹ', g: 'ㅎ', h: 'ㅗ', j: 'ㅓ', k: 'ㅏ', l: 'ㅣ',
    z: 'ㅋ', x: 'ㅌ', c: 'ㅊ', v: 'ㅍ', b: 'ㅠ', n: 'ㅜ', m: 'ㅡ',
    Q: 'ㅃ', W: 'ㅉ', E: 'ㄸ', R: 'ㄲ', T: 'ㅆ', O: 'ㅒ', P: 'ㅖ'
};
const CHO = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const JUNG = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ';
const JONG = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const VV: Record<string, string> = { 'ㅗㅏ': 'ㅘ', 'ㅗㅐ': 'ㅙ', 'ㅗㅣ': 'ㅚ', 'ㅜㅓ': 'ㅝ', 'ㅜㅔ': 'ㅞ', 'ㅜㅣ': 'ㅟ', 'ㅡㅣ': 'ㅢ' };
const CC: Record<string, string> = { 'ㄱㅅ': 'ㄳ', 'ㄴㅈ': 'ㄵ', 'ㄴㅎ': 'ㄶ', 'ㄹㄱ': 'ㄺ', 'ㄹㅁ': 'ㄻ', 'ㄹㅂ': 'ㄼ', 'ㄹㅅ': 'ㄽ', 'ㄹㅌ': 'ㄾ', 'ㄹㅍ': 'ㄿ', 'ㄹㅎ': 'ㅀ', 'ㅂㅅ': 'ㅄ' };
const SPLIT: Record<string, [string, string]> = Object.fromEntries(Object.entries(CC).map(([k, v]) => [v, [k[0], k[1]]]));

/** 영문 자판 글자만으로 된 말이면 한글로 바꾼 것, 아니면 null */
export function engToHangul(s: string): string | null {
    if (!/^[a-zA-Z\s]+$/.test(s)) return null;
    let out = '';
    let cho = '', jung = '', jong = '';
    const flush = () => {
        if (cho && jung) out += String.fromCharCode(0xac00 + (CHO.indexOf(cho) * 21 + JUNG.indexOf(jung)) * 28 + JONG.indexOf(jong));
        else out += cho + jung + jong;
        cho = jung = jong = '';
    };
    for (const ch of s) {
        const j = KEY[ch] ?? KEY[ch.toLowerCase()];
        if (!j) { flush(); out += ch; continue; }
        if (JUNG.includes(j)) {
            if (jong) {
                // 받침이 다음 글자의 첫소리로 넘어간다 (겹받침이면 뒤쪽만)
                const sp = SPLIT[jong];
                const next = sp ? sp[1] : jong;
                jong = sp ? sp[0] : '';
                flush();
                cho = next; jung = j;
            } else if (jung) {
                if (VV[jung + j]) jung = VV[jung + j];
                else { flush(); jung = j; }
            } else jung = j;
        } else {
            if (!cho && !jung) cho = j;
            else if (cho && jung && !jong && JONG.includes(j)) jong = j;
            else if (jong && CC[jong + j]) jong = CC[jong + j];
            else { flush(); cho = j; }
        }
    }
    flush();
    return out;
}
